import { findConsecutiveRepeatedCharacter, findRepeatedWord } from './descriptionValidation';

export interface BankExistingRecord {
  _id?: string;
  code?: string;
  name?: string;
  country?: string;
  sortOrder?: number;
  isActive?: boolean;
  metadata?: Record<string, unknown>;
  usageCount?: number;
  deleteLock?: string | null;
}

export interface BankFormInput {
  name: string;
  country: string;
  description?: string;
  sortOrder: number;
  metadata: Record<string, unknown>;
}

export const BANK_FIELD_LABELS: Record<string, string> = {
  country: 'Country',
  name: 'Bank name',
  code: 'Code',
  ifscPrefix: 'IFSC prefix',
  swiftCode: 'SWIFT / BIC',
  sortOrder: 'Sort order',
  description: 'Description',
};

export const IFSC_PREFIX_PATTERN = /^[A-Z]{4}$/;
export const IFSC_PATTERN = /^[A-Z]{4}0[A-Z0-9]{6}$/;
export const BIC_PATTERN = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
const NAME_PATTERN = /^\p{L}[\p{L}\s&.'()-]*$/u;
const str = (v: unknown) => (v == null ? '' : String(v).trim());

export const usesIfsc = (country: string) => country.toUpperCase() === 'IN';
export const normalizeBankName = (s: unknown) => String(s ?? '').replace(/\s+/g, ' ').trim();
export const sanitizeIfscPrefix = (s: string) => s.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4);
export const sanitizeIfsc = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);
export const sanitizeBic = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);

/** Code convention (mirrors the server): IFSC prefix for Indian banks, SWIFT bank code for other international banks, else from the name. */
export function previewBankCode(form: BankFormInput): string {
  const country = str(form.country).toUpperCase();
  if (usesIfsc(country)) return str(form.metadata.ifscPrefix).toUpperCase();
  if (form.metadata.internationalTransfers === true) return str(form.metadata.swiftCode).toUpperCase().slice(0, 4);
  return normalizeBankName(form.name).toUpperCase().replace(/&/g, ' AND ').replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 20);
}

export function bankOrderOf(row: BankExistingRecord): number {
  const top = Number(row.sortOrder);
  if (Number.isInteger(top) && top > 0) return top;
  const meta = Number(row.metadata?.sortOrder);
  return Number.isInteger(meta) && meta > 0 ? meta : 0;
}

export function nextBankSortOrder(existing: BankExistingRecord[], editingId?: string) {
  return Math.max(0, ...existing.filter((r) => r._id !== editingId).map(bankOrderOf)) + 1;
}

export function validateBankForm(
  form: BankFormInput,
  ctx: { existing: BankExistingRecord[]; editingId?: string; code: string },
): Record<string, string> {
  const errors: Record<string, string> = {};
  const others = ctx.existing.filter((r) => r._id !== ctx.editingId);
  const original = ctx.editingId ? ctx.existing.find((r) => r._id === ctx.editingId) : undefined;
  const country = str(form.country).toUpperCase();
  const name = normalizeBankName(form.name);
  const meta = form.metadata;

  if (!country) errors.country = 'Choose the country the bank operates in';

  if (!name) errors.name = 'Bank name is required';
  else if (name !== original?.name && (name.length < 2 || name.length > 100)) errors.name = 'Must be 2–100 characters';
  else if (name !== original?.name && !NAME_PATTERN.test(name)) errors.name = "Letters, spaces and & . ' ( ) - only — no numbers";
  else {
    const clash = others.find((r) => str(r.country).toUpperCase() === country && normalizeBankName(r.name).toLowerCase() === name.toLowerCase());
    if (clash) errors.name = `"${clash.name}" already exists in ${country}`;
  }

  const prefix = str(meta.ifscPrefix).toUpperCase();
  if (country && usesIfsc(country)) {
    if (!prefix) errors.ifscPrefix = 'Required for Indian banks — first 4 letters of any branch IFSC';
    else if (!IFSC_PREFIX_PATTERN.test(prefix)) errors.ifscPrefix = 'Exactly 4 letters (e.g. HDFC, SBIN)';
    else {
      const clash = others.find((r) => str(r.metadata?.ifscPrefix).toUpperCase() === prefix);
      if (clash) errors.ifscPrefix = `${prefix} is already used by "${clash.name}"`;
    }
  }

  if (meta.internationalTransfers === true) {
    const swift = str(meta.swiftCode).toUpperCase();
    if (!swift) errors.swiftCode = 'Required for international transfers — or turn the switch off';
    else if (!BIC_PATTERN.test(swift)) errors.swiftCode = '8 or 11 characters: bank (4 letters) + country (2) + location (2) + optional branch (3)';
    else if (country && swift.slice(4, 6) !== country) errors.swiftCode = `Characters 5–6 must be the bank country (${country}), not ${swift.slice(4, 6)}`;
    else {
      const clash = others.find((r) => str(r.metadata?.swiftCode).toUpperCase().slice(0, 8) === swift.slice(0, 8));
      if (clash) errors.swiftCode = `${swift.slice(0, 8)} is already used by "${clash.name}"`;
    }
  }

  if (ctx.code && !errors.ifscPrefix && !errors.swiftCode) {
    const clash = others.find((r) => str(r.code).toUpperCase() === ctx.code);
    if (clash) errors.code = `Code ${ctx.code} is already used by "${clash.name}"`;
  }

  if (!Number.isInteger(form.sortOrder) || form.sortOrder < 1) {
    errors.sortOrder = 'Enter a whole number of 1 or more';
  } else {
    const clash = others.find((r) => bankOrderOf(r) === form.sortOrder);
    if (clash) errors.sortOrder = `Already used by "${clash.name}" — next available is ${nextBankSortOrder(ctx.existing, ctx.editingId)}`;
  }

  const description = str(form.description);
  if (description.length > 500) errors.description = 'Must be 500 characters or fewer';
  else if (description) {
    const run = findConsecutiveRepeatedCharacter(description);
    const word = findRepeatedWord(description);
    if (run) errors.description = `No repeated characters ("${run.slice(0, 4)}")`;
    else if (word) errors.description = `No repeated words ("${word}")`;
  }
  return errors;
}
